import { TrashBaseIcon } from '@tableau/icons/TrashBaseIcon';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { DeleteBridgePoolDialogBody, DeleteBridgePoolDialogProps } from '~src/settings/bridge-settings/sections/bridgeMultiPool/DeleteBridgePoolDialog';
import { getRoutingFiltersQueryKey } from '~src/settings/bridge-settings/sections/bridgeRoutingFilters/useRoutingFilters';
import { IDeleteEdgePoolsResult } from '~src/settings/bridge-settings/serverApi/requests/DeleteEdgePoolsRequest';
import { EdgePool } from '~src/settings/bridge-settings/serverApi/resources/EdgePool';

import { Domain } from '../types/Domain';
import { UserDefinedPoolModel } from '../types/PoolModel';
import { getErrorMessage } from '../utils/EdgePoolErrorUtilities';

export type DeletePoolButtonProps = Readonly<{
  pool: UserDefinedPoolModel;
  siteId: string;
  refreshEdgePools: () => void;
}>;

export function DeletePoolButton(props: DeletePoolButtonProps) {
  const queryClient = useQueryClient();
  return (
    <span className={'tb-data-grid-icon tb-padded'} data-tb-test-id={'pool-delete-cell'}>
      <ButtonWidget
        title={Messages.deleteMessage()}
        buttonType={ButtonType.Icon}
        testId={'delete-pool-button'}
        onClick={() => openDeletePoolDialog(props.pool, props.siteId, props.refreshEdgePools, queryClient)}
      >
        <TrashBaseIcon size={14} alt={Messages.deleteMessage()} />
      </ButtonWidget>
    </span>
  );
}

function openDeletePoolDialog(pool: UserDefinedPoolModel, siteId: string, refreshEdgePools: () => void, queryClient: QueryClient) {
  const action = (_: DeleteBridgePoolDialogProps) => {
    EdgePool.deletePools({ poolIds: [pool.id], siteId: siteId, bridgeSettingsVersion: '0' })
      .then((result: IDeleteEdgePoolsResult) => {
        refreshEdgePools();
        if (result.success) {
          // The backend cascade-deletes the pool's routing filters. Invalidate the cached
          // routing-filters query so the removed filters refetch and drop out of the UI.
          queryClient.invalidateQueries(getRoutingFiltersQueryKey(siteId));
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.bridgeSiteSettingsDeletePoolSuccess() });
        }
        if (result.error) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getErrorMessage(result.error.errorCode, {poolName: pool.displayName, domainName: null, agentName: null})
          });
        }
      });
    return Promise.resolve();
  };

  const domains: Domain[] = Object.values(pool.domains);

  const dialogInstance = openReactActionDialog({
    title: Messages.bridgeSiteSettingsDeletePool(),
    bodyComponent: DeleteBridgePoolDialogBody,
    initialBodyProps: {
      poolName: pool.displayName,
      hasAgents: Object.values(pool.agents).length > 0,
      hasDomains: domains.length > 0,
      domains: domains
    },
    initialDialogState: {},
    actionName: Messages.deleteMessage(),
    action: action,
    isActionDelete: true,
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width375px
  });

  return Promise.resolve(dialogInstance.result);
}
